// @flow

import React, { type ComponentType } from 'react';
import { get } from 'lodash';

import { TrialInProgressBanner as CompaniesTrialInProgressBanner } from '@zapier/design-system-company';
import { daysBetween, daysFromNow } from '@zapier/date';

import * as Account from 'app/entities/Account';
import * as Plan from 'app/entities/Plan';
import * as Profile from 'app/entities/Profile';
import Button from 'app/common/components/Button';
import DismissContainer from 'app/viewport/components/TopBar/DismissContainer';
import JobsCta from 'app/viewport/components/TopBar/JobsCta';
import Link from 'app/common/components/Link';
import LinkContainer from 'app/common/components/LinkContainer';
import TeamsPricingContainer from 'app/team/components/TeamsPricingContainer';
import ToggleTeamsUpgradeButtonContainer from 'app/viewport/components/TopBar/ToggleTeamsUpgradeButtonContainer';
import TrialMessageContainer from 'app/viewport/components/TopBar/TrialMessageContainer';
import { EVENT_TRACK_TRIAL_BANNER } from 'app/billing/constants';
import { TopBarJobsCta } from 'app/viewport/components/TopBar/TopBar';
import { isOnSharedZapRoute } from 'app/shared-zap/utils';
import { isRefreshRequired } from 'app/refresh/Selectors';
import { isVBOrLegacyUrl } from 'app/developer-v3/utils/utils';
import { buildPlanChangeUrl, isOnUpgradeRoute } from 'app/billing/utils';
import {
  isEligibleForAnnualUpgrade,
  selectYearlyDiscountForCurrentPlan,
} from 'app/billing/Selectors';

import { type KindType } from 'app/common/types';

export type TopBarConfigType = {
  kind?: KindType,
  shouldRender: ({
    state: {},
    currentAccount: {},
    currentProfile: {},
  }) => boolean,
  mapStateToProps?: ({
    state: {},
    currentAccount: {},
    currentProfile: {},
  }) => {},
  ChildComponent: ?ComponentType<*>,
};

export const TopBarConfigsByName: { [string]: TopBarConfigType } = {
  upgrades: {
    shouldRender: ({ state }) => isRefreshRequired(state),
    ChildComponent: () => {
      return (
        <div>
          Hi! We just made some upgrades to Zapier for you.{' '}
          <Button kind="as-link" onClick={() => window.location.reload()}>
            Refresh now
          </Button>{' '}
          to get the new version.
        </div>
      );
    },
  },
  bannerMessage: {
    shouldRender: ({ currentProfile }) =>
      Profile.selectors.bannerMessage(currentProfile),
    mapStateToProps: ({ currentProfile }) => ({
      bannerMessage: Profile.selectors.bannerMessage(currentProfile),
    }),
    ChildComponent: ({ bannerMessage }) => {
      return <div dangerouslySetInnerHTML={{ __html: bannerMessage }} />;
    },
  },
  // This config mostly applies to admins, but not exclusively.
  // Any configs below this one effectively don't apply if the
  // account `isUnlimited` since this one will "render" first.
  unlimited: {
    shouldRender: ({ currentAccount }) =>
      Account.selectors.isUnlimited(currentAccount),
    ChildComponent: null,
  },
  teamsDowngraded: {
    shouldRender: ({ currentAccount, state }) =>
      !isVBOrLegacyUrl(state) &&
      !Account.selectors.isTeam(currentAccount) &&
      Account.selectors.wasTeam(currentAccount) &&
      daysFromNow(Account.selectors.teamAccountEndDate(currentAccount)) <= 7,
    kind: 'teams',
    ChildComponent: () => {
      return (
        <div>
          You’re not on a team account anymore, so some features are no longer
          available.{' '}
          <Link href="/app/billing/plans/upgrade-to-team">Upgrade</Link> or{' '}
          <Link href="/help/account/teams/cancel-your-zapier-for-teams-plan">
            learn more
          </Link>
          .
        </div>
      );
    },
  },
  companiesTrial: {
    shouldRender: ({ currentAccount, state }) =>
      !isVBOrLegacyUrl(state) &&
      Account.selectors.isTrialingCompanies(currentAccount),
    mapStateToProps: ({ currentAccount }) => ({
      daysRemaining: Account.selectors.companiesTrialDaysLeft(currentAccount),
    }),
    kind: 'companies',
    ChildComponent: ({ daysRemaining }) => {
      return <CompaniesTrialInProgressBanner daysRemaining={daysRemaining} />;
    },
  },
  teamsTrial: {
    shouldRender: ({ currentAccount, state }) =>
      !isVBOrLegacyUrl(state) &&
      Account.selectors.isTrialingTeams(currentAccount),
    mapStateToProps: ({ currentAccount }) => ({
      trialDays: Account.selectors.teamsTrialDaysLeft(currentAccount),
    }),
    kind: 'teams',
    ChildComponent: ({ trialDays }) => {
      return (
        <ToggleTeamsUpgradeButtonContainer>
          {({ shouldAutoUpgradeToTeams }) => (
            <span>
              You’re on a free trial of Zapier for Teams and have {trialDays}{' '}
              days left in your trial. After that you will{' '}
              {shouldAutoUpgradeToTeams ? (
                <span>
                  be automatically upgraded and charged{' '}
                  <TeamsPricingContainer />
                </span>
              ) : (
                'lose access to Team features.'
              )}
            </span>
          )}
        </ToggleTeamsUpgradeButtonContainer>
      );
    },
  },
  trial: {
    shouldRender: ({ currentAccount, state }) =>
      !isVBOrLegacyUrl(state) &&
      !isOnUpgradeRoute(state) &&
      Account.selectors.isTrialing(currentAccount),
    mapStateToProps: ({ currentAccount, state }) => {
      const nextPlan = Plan.selectors.all.nextPlanTierByYear(state);
      return {
        nextPlanName: get(nextPlan, ['data', 'name'], 'starter').toLowerCase(),
        nextPlanAmount: Plan.selectors.formattedAmountPerMonth(nextPlan),
        trialDays: Account.selectors.trialDaysLeft(currentAccount),
      };
    },
    kind: 'justify-between',
    ChildComponent: ({ nextPlanAmount, nextPlanName, trialDays }) => {
      if (trialDays > 30) {
        return (
          <React.Fragment>
            <div>You have {trialDays} days left in your extended trial.</div>
            <TopBarJobsCta kind="bubble-background" />
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          <TrialMessageContainer
            nextPlanAmount={nextPlanAmount}
            nextPlanName={nextPlanName}
            trialDays={trialDays}
          />
          <TopBarJobsCta kind="bubble-background" />
        </React.Fragment>
      );
    },
  },
  postTrial: {
    shouldRender: ({ currentAccount, state }) =>
      !isVBOrLegacyUrl(state) &&
      !Account.selectors.isTrialing(currentAccount) &&
      !!Account.selectors.trialEndDate(currentAccount) &&
      Account.selectors.isFree(currentAccount) &&
      !!Plan.selectors.all.nextPlanTier(state) &&
      daysBetween(Account.selectors.trialEndDate(currentAccount), Date.now()) <=
        14,
    mapStateToProps: ({ state }) => ({
      nextPlanAmount: Plan.selectors.formattedAmountPerMonth(
        Plan.selectors.all.nextPlanTier(state)
      ),
    }),
    kind: 'justify-between',
    ChildComponent: ({ nextPlanAmount }) => {
      return (
        <div>
          <LinkContainer
            href="/app/billing/plans"
            trackData={{
              in_trial: false,
            }}
            trackMessage={EVENT_TRACK_TRIAL_BANNER}
          >
            Upgrade now for {nextPlanAmount}
          </LinkContainer>{' '}
          to use Filters, Formatters, Lead Score, and much more!
        </div>
      );
    },
  },
  annual: {
    shouldRender: ({ state }) =>
      !isVBOrLegacyUrl(state) && isEligibleForAnnualUpgrade(state),
    mapStateToProps: ({ currentAccount, state }) => ({
      planName: Account.selectors.planName(currentAccount),
      yearlyDiscount: selectYearlyDiscountForCurrentPlan(state),
    }),
    ChildComponent: ({ planName, yearlyDiscount }) => {
      const percent = yearlyDiscount ? `${yearlyDiscount}%` : '';
      const url = buildPlanChangeUrl('change', planName);
      return (
        <div>
          <Link href={url}>Save {percent}</Link> by switching to an annual
          subscription. <Link href={url}>Switch Now</Link>
        </div>
      );
    },
  },
  jobs: {
    shouldRender: ({ currentProfile, currentAccount, state }) =>
      !isVBOrLegacyUrl(state) &&
      !isOnSharedZapRoute(state) &&
      !isOnUpgradeRoute(state) &&
      !Profile.selectors.hasSeenJobsCta(currentProfile) &&
      !Account.selectors.isCompany(currentAccount),
    kind: 'subtle justify-end',
    mapStateToProps: ({ currentProfile }) => ({
      profileId: Profile.selectors.id(currentProfile),
    }),
    ChildComponent: ({ profileId }) => {
      const dismissButton = !!profileId && (
        <React.Fragment>
          <DismissContainer
            kind="end"
            profileId={profileId}
            trackingKey="jobs_cta"
          />
        </React.Fragment>
      );
      return (
        <div>
          <JobsCta />
          {dismissButton}
        </div>
      );
    },
  },
};

export default [
  TopBarConfigsByName.upgrades,
  TopBarConfigsByName.bannerMessage,
  TopBarConfigsByName.unlimited,
  TopBarConfigsByName.teamsDowngraded,
  TopBarConfigsByName.companiesTrial,
  TopBarConfigsByName.teamsTrial,
  TopBarConfigsByName.trial,
  TopBarConfigsByName.postTrial,
  TopBarConfigsByName.annual,
  TopBarConfigsByName.jobs,
];
